import React from 'react';
import { MapPin, Edit2 } from 'lucide-react';
import { POI_TYPES } from '../../../../../constants/poiTypes';
import { formatDistance } from '../../../../../utils/routeHelpers';
import { useNavigate } from 'react-router-dom';
import { useSurvey } from '../../../../../contexts/SurveyContext';
import { logError } from '../../../../../utils/logger';

const truncateText = (text, maxLength = 60) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

export const TimelinePoint = ({ point, type = 'poi', Icon, distance = 0, timestamp }) => {
  if (!point) {
    logError('TimelinePoint received null point');
    return null;
  }

  const navigate = useNavigate();
  const { activeSurvey } = useSurvey();

  const getPointStyles = () => {
    switch (type) {
      case 'origin':
        return {
          border: 'border-green-500',
          icon: 'text-green-500'
        };
      case 'destination':
        return {
          border: 'border-red-500',
          icon: 'text-red-500'
        };
      default:
        return {
          border: `border-[${POI_TYPES[point.type]?.color || '#F59E0B'}]`,
          icon: `text-[${POI_TYPES[point.type]?.color || '#F59E0B'}]`
        };
    }
  };

  const styles = getPointStyles();
  const coordinates = React.useMemo(() => {
    if (type === 'poi') {
      return point?.coordinates ? 
        `${point.coordinates.latitude.toFixed(6)}, ${point.coordinates.longitude.toFixed(6)}` :
        'No coordinates';
    }
    
    if (!point?.trackPoints?.length) {
      return type === 'origin' ? 'Starting Point' : 'End Point';
    }
    
    const trackPoint = type === 'origin' ? 
      point.trackPoints[0] : 
      point.trackPoints[point.trackPoints.length - 1];
      
    return trackPoint ? 
      `${trackPoint.latitude.toFixed(6)}, ${trackPoint.longitude.toFixed(6)}` :
      'Invalid coordinates';
  }, [point, type]);

  return (
    <div className="relative flex items-start gap-4 mb-8">
      <div className={`z-10 w-8 h-8 rounded-full bg-[#2A2B2E] border-2 ${styles.border} flex items-center justify-center shrink-0`}>
        <Icon className={`w-4 h-4 ${styles.icon}`} />
      </div>
      <div className="flex-1 bg-[#2A2B2E] rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-white font-medium">
              {type === 'poi' 
                ? `${POI_TYPES[point?.type]?.prefix || 'POI'}-${point?.id?.slice(-4) || 'Unknown'}`
                : type === 'origin' ? 'Origin' : 'Destination'
              }
            </span>
            <span className="text-sm text-gray-400">
              {formatDistance(distance)}
            </span>
          </div>
          {type === 'poi' && (
            <div className="flex gap-2">
              <button 
                onClick={() => {
                  if (point?.coordinates) {
                    navigate('/map', { 
                      state: { 
                        center: {
                          lat: Number(point.coordinates.latitude),
                          lng: Number(point.coordinates.longitude)
                        },
                        poiId: point.id,
                        surveyId: activeSurvey?.id || point.surveyId
                      }
                    });
                  }
                }}
                className="p-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
                title="View on Map"
              >
                <MapPin className="w-4 h-4" />
              </button>
              <button 
                onClick={() => {
                  navigate(`/pois`, { 
                    state: { 
                      surveyId: activeSurvey?.id || point.surveyId,
                      selectedPoi: point.id
                    }
                  });
                }}
                className="p-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg transition-colors"
                title="Edit POI"
              >
                <Edit2 className="w-4 h-4 text-gray-400" />
              </button>
              {timestamp && (
                <span className="text-xs text-gray-500 block mt-1">
                  {new Date(timestamp).toLocaleString()}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="text-gray-400 mb-2">
          {point?.address || coordinates}
        </div>
        {type === 'poi' && (
          <>
            {point?.measurements?.length > 0 && (
              <div className="text-sm text-gray-500">
                Dimensions: {point.measurements.map(m => 
                  `${m.value}${m.unit}`
                ).join(' × ')}
              </div>
            )}
            {point?.notes && (
              <div className="text-sm text-gray-500">
                Notes: {truncateText(point.notes)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};